import { useEffect, useRef } from 'react'
import {
  getLocation,
  getStoredGeoFor,
  saveGeoToStore,
  saveTimezoneToStore,
} from 'helpers/geo'
import { ActorType, Geo, UserFull } from '@wellesley/openapi'
import { useSafeUserUpdate } from '@wellesley/user/hooks/useSafeUserUpdate'
import { logger } from 'helpers/logger'
import { toast } from '@wellesley/system-toast'
import { resolveUserTimezone } from 'helpers/timezone/resolveUserTimezone'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import useTimezones from '../useTimezones'

type ExtendedUser = UserFull & {
  channel?: boolean
  actorType?: ActorType | null
}

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(isToday)

type UseProfileAutoUpdateProps = {
  user?: UserFull | null
}

const useProfileAutoUpdate = ({ user }: UseProfileAutoUpdateProps = {}) => {
  const userId = user?.id
  const { options: timezoneOptions } = useTimezones()
  const updateUser = useSafeUserUpdate()
  const pendingRef = useRef(false)

  useEffect(() => {
    if (!user || !userId || pendingRef.current) return

    const storedGeo = getStoredGeoFor(userId)
    const isOutdated = !storedGeo?.date || !dayjs(storedGeo.date).isToday()

    const shouldUpdateLocation =
      user.location?.autoUpdate && (isOutdated || !storedGeo?.geo)
    const shouldUpdateTimezone =
      user.timezone?.autoUpdate && (isOutdated || !storedGeo?.tz)

    if (!shouldUpdateLocation && !shouldUpdateTimezone) return

    pendingRef.current = true

    logger.debug('Profile auto update:', {
      shouldUpdateLocation,
      shouldUpdateTimezone,
    })

    const buildPayload = (geo?: Geo, ianaTimezone?: string) => {
      const { username, displayName, summary, fields } = user
      const extendedUser = user as ExtendedUser

      return {
        userId,
        data: {
          username,
          displayName,
          fields: fields || {},
          summary,
          avatarId: user.avatar?.uploadId || null,
          headerId: user.header?.uploadId || null,
          actorType: extendedUser.actorType || null,
          birthday: user.birthday || null,
          location: geo
            ? {
                name: user.location?.name ?? null,
                autoUpdate: user.location?.autoUpdate ?? false,
                show: user.location?.show ?? false,
                geo,
              }
            : user.location || null,
          timezone: ianaTimezone
            ? {
                ianaTimezone,
                autoUpdate: user.timezone?.autoUpdate ?? false,
                show: user.timezone?.show ?? false,
              }
            : user.timezone || null,
        },
      }
    }

    const ianaTimezone = shouldUpdateTimezone
      ? resolveUserTimezone(timezoneOptions)
      : undefined

    if (shouldUpdateLocation) {
      getLocation({
        onSuccess: (position) => {
          const geo = position.coords
          void updateUser(buildPayload(geo, ianaTimezone))
          saveGeoToStore(geo, userId)
          if (ianaTimezone) saveTimezoneToStore(ianaTimezone, userId)
        },
        onError: (e) => {
          const message =
            e.code === 1
              ? 'Location access denied. Enable it for automatic detection'
              : `Update location error: ${e.message}`
          toast.error(message)

          if (ianaTimezone) {
            void updateUser(buildPayload(undefined, ianaTimezone))
            saveTimezoneToStore(ianaTimezone, userId)
          }
        },
      })
    } else if (ianaTimezone) {
      void updateUser(buildPayload(undefined, ianaTimezone))
      saveTimezoneToStore(ianaTimezone, userId)
    }
  }, [user?.location?.autoUpdate, user?.timezone?.autoUpdate, userId])
}

export default useProfileAutoUpdate
